import React, { useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SyncIcon from '@mui/icons-material/Sync';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FlagIcon from '@mui/icons-material/Flag';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ShieldIcon from '@mui/icons-material/Shield';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import PrintIcon from '@mui/icons-material/Print';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import TrafficIcon from '@mui/icons-material/Traffic';
import dayjs from 'dayjs';

import { generateCarePlanFromAI } from '../../../utils/services/Careplans/carePlanAiService';
import { generateMealPlanFromAI } from '../../../utils/services/Careplans/mealPlanAiService';

import PageTitle from '../../../components/Structure/Title/PageTitle';
import PatientAutocomplete from '../../../components/Advanced/Autocomplete/PatientAutocomplete';
import { PatientContext } from '../../../context/PatientContext/PatientContext';
import { CarePlanContext } from '../../../context/CarePlanContext/CarePlanContext';
import { AdvancedMdContext } from '../../../context/AdvancedMD/AdvancedMdContext';

const HEADER_BG = '#607d8b';

function SectionHeader({ title }) {
  return (
    <Box
      sx={{
        backgroundColor: HEADER_BG,
        color: '#fff',
        px: 2,
        py: 1.5,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        {title}
      </Typography>
    </Box>
  );
}

function Careplan() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const {
    patientDetails,
    memberships,
    fetchPatientDetails,
    fetchPatientsMemberships,
    loading: patientLoading,
  } = useContext(PatientContext);

  const {
    Careplans,
    fetchCareplans,
    loading: carePlanLoading,
  } = useContext(CarePlanContext);

  const {
    matchPatientInAdvancedMd,
    patientLookupResults,
    lookupLoading,
    lookupError,
    // Allergies
    loadAllergies,
    allergies,
    allergiesLoading,
    // Clinical Notes
    loadAnnualWellnessNotes,
    annualWellnessNotes,
    annualWellnessLoading,
    loadFollowUpExamNotes,
    followUpExamNotes,
    followUpExamLoading,
    loadTelehealthNotes,
    telehealthNotes,
    telehealthLoading,
    loadHospitalDischargeNotes,
    hospitalDischargeNotes,
    hospitalDischargeLoading,
  } = useContext(AdvancedMdContext);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientData, setPatientData] = useState(null);

  // ── Pre-carga de paciente vía navigation state ──────────────────────
  // Cuando alguien navega aquí con `navigate('/care-plan', { state: { patient } })`
  // — por ejemplo desde el quick action "Add Care Plan" en PatientInfoPage o
  // PatientActionsBar — recibimos el paciente en location.state y lo seleccionamos
  // automáticamente para saltar el paso de búsqueda manual.
  const location = useLocation();
  const navigate = useNavigate();
  const preselectAppliedRef = useRef(false);

  // When a patient is selected from autocomplete, fetch all data
  const handlePatientSelect = async (patient) => {
    if (!patient) {
      handleClear();
      return;
    }
    setSelectedPatient(patient);

    const cipId = patient.memberId;
    fetchPatientDetails(cipId);
    fetchCareplans(cipId);
    fetchPatientsMemberships(cipId);
  };

  useEffect(() => {
    if (preselectAppliedRef.current) return;
    const incoming = location.state?.patient;
    if (incoming?.memberId) {
      preselectAppliedRef.current = true;
      handlePatientSelect(incoming);
      // Limpiar el state para que un refresh manual no vuelva a auto-seleccionar.
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Map patientDetails into display data
  useEffect(() => {
    if (Array.isArray(patientDetails) && patientDetails.length > 0 && selectedPatient) {
      const p = patientDetails[0];
      setPatientData({
        cipId: p.varCipId || '',
        memberId: p.memberId || '',
        fullName: p.memberFullName || '',
        lastName: p.lastName || '',
        firstName: p.firstName || '',
        middleName: p.middleName || '',
        dob: p.dateOfBirth?.split('T')[0] || '',
        age: p.age ?? '',
        gender: p.gender === 'M' ? 'Male' : p.gender === 'F' ? 'Female' : p.gender || '',
        genderCode: p.gender || '',
        email: p.memberEmail || 'Not available',
        phone: p.memberPhone || 'Not available',
        mobile: p.memberMobile || '',
        careManager: p.careManager || '',
        language: p.language || '',
        role: p.role || '',
        status: p.status ?? '',
        dischargeId: p.dischargeId,
        numAdmittedId: p.numAdmittedId,
        numEmergencyId: p.numEmergencyId,
        numHighCostId: p.numHighCostId,
        numHighRiskId: p.numHighRiskId,
      });
    }
  }, [patientDetails, selectedPatient]);

  // Status chips
  const statusIndicators = useMemo(() => {
    if (!patientData) return [];
    return [
      { label: 'High Risk', found: patientData.numHighRiskId !== 0, color: 'darkred', icon: <ErrorIcon sx={{ color: '#fff' }} /> },
      { label: 'High Cost', found: patientData.numHighCostId !== 0, color: 'purple', icon: <WarningIcon sx={{ color: '#fff' }} /> },
      { label: 'Discharged', found: patientData.dischargeId !== 0, color: 'red', icon: <ErrorIcon sx={{ color: '#fff' }} /> },
      { label: 'Admitted', found: patientData.numAdmittedId !== 0, color: 'green', icon: <CheckCircleIcon sx={{ color: '#fff' }} /> },
      { label: 'Emergency', found: patientData.numEmergencyId !== 0, color: 'orange', icon: <WarningIcon sx={{ color: '#fff' }} /> },
    ];
  }, [patientData]);

  // AdvancedMD match
  const matchPayload = useMemo(() => {
    if (!patientData) return null;
    const { firstName, lastName, fullName, dob, genderCode } = patientData;
    if (!firstName && !lastName && !fullName) return null;
    let dobIso = null;
    if (dob) {
      const d = new Date(dob);
      if (!Number.isNaN(d.getTime())) dobIso = d.toISOString();
    }
    return { firstName, lastName, dob: dobIso, sex: genderCode };
  }, [patientData]);

  useEffect(() => {
    if (!matchPayload) return;
    matchPatientInAdvancedMd(matchPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchPayload]);

  const bestMatch = useMemo(
    () => (patientLookupResults && patientLookupResults.length > 0 ? patientLookupResults[0] : null),
    [patientLookupResults]
  );
  const candidate = bestMatch?.candidate ?? null;
  const chartValue =
    candidate &&
    (candidate.chart ||
      candidate.Chart ||
      candidate.chartId ||
      candidate.ChartId ||
      candidate.ChartID ||
      candidate.chart_id);

  // Derive normalized AdvancedMD patient ID (strip "pat" prefix)
  const advancedMdPatientId = useMemo(() => {
    if (!candidate?.id) return null;
    const rawId = String(candidate.id);
    return rawId.startsWith('pat') ? rawId.slice(3) : rawId;
  }, [candidate]);

  // Fetch clinical notes + allergies when AdvancedMD patient is matched
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadAllergies(advancedMdPatientId);
    loadAnnualWellnessNotes(advancedMdPatientId);
    loadFollowUpExamNotes(advancedMdPatientId);
    loadTelehealthNotes(advancedMdPatientId);
    loadHospitalDischargeNotes(advancedMdPatientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedMdPatientId]);

  // Merge all clinical notes into a single sorted array
  const allClinicalNotes = useMemo(() => {
    const merge = (notes, type) =>
      (notes || []).map((n) => ({ ...n, _type: type }));
    const combined = [
      ...merge(annualWellnessNotes, 'Annual Wellness'),
      ...merge(followUpExamNotes, 'Follow Up Exam'),
      ...merge(telehealthNotes, 'Telehealth'),
      ...merge(hospitalDischargeNotes, 'Hospital Discharge'),
    ];
    combined.sort((a, b) => {
      const da = a.noteDateTime ? new Date(a.noteDateTime) : 0;
      const db = b.noteDateTime ? new Date(b.noteDateTime) : 0;
      return db - da;
    });
    return combined;
  }, [annualWellnessNotes, followUpExamNotes, telehealthNotes, hospitalDischargeNotes]);

  const notesLoading =
    annualWellnessLoading || followUpExamLoading || telehealthLoading || hospitalDischargeLoading;

  // ========= AI Care Plan Dialog State =========
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiGoals, setAiGoals] = useState('');
  const [aiHabits, setAiHabits] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiDietaryPreferences, setAiDietaryPreferences] = useState('');
  const [aiCulturalDiet, setAiCulturalDiet] = useState('');
  const [mealPlanResult, setMealPlanResult] = useState(null);
  const [mealPlanLoading, setMealPlanLoading] = useState(false);
  const [mealPlanError, setMealPlanError] = useState(null);
  const [wizardMode, setWizardMode] = useState(null); // null | 'carePlanOnly' | 'carePlanAndMeal'
  const [activeStep, setActiveStep] = useState(0);

  const handleClear = () => {
    setSelectedPatient(null);
    setPatientData(null);
    setAiResult(null);
    setAiError(null);
    setMealPlanResult(null);
    setMealPlanError(null);
  };

  const handleCreateCarePlan = () => {
    setDialogOpen(true);
    setAiResult(null);
    setAiError(null);
    setMealPlanResult(null);
    setMealPlanError(null);
    setWizardMode(null);
    setActiveStep(0);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setWizardMode(null);
    setActiveStep(0);
  };

  const handleSelectMode = (mode) => {
    setWizardMode(mode);
    setActiveStep(0);
  };

  const handleWizardNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleWizardBack = () => {
    if (activeStep === 0) {
      setWizardMode(null);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleGenerateAiPlan = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    setMealPlanResult(null);
    setMealPlanError(null);

    try {
      const result = await generateCarePlanFromAI({
        patient: patientData,
        allergies: allergies || [],
        clinicalNotes: allClinicalNotes || [],
        goals: aiGoals,
        habits: aiHabits,
      });

      if (result.carePlan) {
        setAiResult(result.carePlan);
        setDialogOpen(false);
        setAiLoading(false);

        // Chain meal plan generation only if user chose both
        if (wizardMode === 'carePlanAndMeal') {
          setMealPlanLoading(true);
          try {
            const mealResult = await generateMealPlanFromAI({
              patient: patientData,
              allergies: allergies || [],
              carePlanResult: result.carePlan,
              dietaryPreferences: aiDietaryPreferences,
              culturalDiet: aiCulturalDiet,
            });

            if (mealResult.mealPlan) {
              setMealPlanResult(mealResult.mealPlan);
            } else {
              setMealPlanError('AI returned an empty meal plan response. The care plan is still valid.');
            }
          } catch (mealErr) {
            console.error('[handleGenerateAiPlan] Meal plan error:', mealErr);
            setMealPlanError(mealErr.message || 'Failed to generate meal plan. The care plan is still valid.');
          } finally {
            setMealPlanLoading(false);
          }
        }
      } else {
        setAiError('AI returned an empty or unparseable response. Please try again.');
        setAiLoading(false);
      }
    } catch (err) {
      console.error('[handleGenerateAiPlan] Error:', err);
      setAiError(err.message || 'Failed to generate care plan.');
      setAiLoading(false);
    }
  }, [patientData, allergies, allClinicalNotes, aiGoals, aiHabits, aiDietaryPreferences, aiCulturalDiet, wizardMode]);

  const handleCopyToClipboard = useCallback(() => {
    if (!aiResult) return;
    const text = JSON.stringify(aiResult, null, 2);
    navigator.clipboard.writeText(text);
  }, [aiResult]);

  const handlePrintSection = useCallback((sectionId, title) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const patientInfo = patientData
      ? `<p style="margin:0;color:#555;">Patient: <strong>${patientData.fullName || ''}</strong> | DOB: ${patientData.dob || ''} | Age: ${patientData.age || ''} | Gender: ${patientData.gender || ''}</p>`
      : '';
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
      h1 { color: #085394; font-size: 22px; margin-bottom: 4px; }
      .patient-info { margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #085394; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 13px; }
      th { background-color: #f5f5f5; }
      @media print { body { padding: 0; } }
    </style></head><body>
      <h1>${title}</h1>
      <div class="patient-info">${patientInfo}</div>
      <div>${element.innerHTML}</div>
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  }, [patientData]);

  const loading = patientLoading || carePlanLoading;

  const Field = ({ label, value }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || '—'}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: isSmallScreen ? '95%' : '90%',
        maxWidth: 1200,
        margin: 'auto',
        mt: 4,
        p: 3,
      }}
    >
      <PageTitle translationKey="CarePlan.CarePlan" />

      {/* Search */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
        <PatientAutocomplete
          onPatientSelect={handlePatientSelect}
          sx={{ flex: 1, maxWidth: 500 }}
        />
        {selectedPatient && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            sx={{ textTransform: 'none' }}
          >
            {t('clear', 'Clear')}
          </Button>
        )}
      </Box>

      {/* Loading */}
      {loading && selectedPatient && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Patient Data Sections */}
      {patientData && !loading && (
        <>
          {/* Patient Information */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader title={t('CarePlan.patientInfo.title', 'Patient Information')} />
            <CardContent>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(3, 1fr)',
                  gap: 3,
                }}
              >
                {/* Column 1: Basic Info */}
                <Box>
                  <Field label={t('CarePlan.patientInfo.ID', 'CIP ID')} value={patientData.cipId} />
                  <Field label={t('CarePlan.patientInfo.name', 'Full Name')} value={patientData.fullName} />
                  <Field label={t('CarePlan.patientInfo.Gender', 'Gender')} value={patientData.gender} />
                  <Field label={t('CarePlan.patientInfo.birthdate', 'Date of Birth')} value={patientData.dob} />
                  <Field label={t('CarePlan.patientInfo.Age', 'Age')} value={patientData.age} />
                </Box>

                {/* Column 2: Contact */}
                <Box>
                  <Field label={t('CarePlan.patientInfo.Email', 'Email')} value={patientData.email} />
                  <Field label={t('CarePlan.patientInfo.phone', 'Phone')} value={patientData.phone} />
                  <Field label={t('CarePlan.patientInfo.mobile', 'Mobile')} value={patientData.mobile} />
                </Box>

                {/* Column 3: Clinical Team */}
                <Box>
                  <Field label={t('CarePlan.patientInfo.careManager', 'Care Manager')} value={patientData.careManager} />
                  <Field label={t('CarePlan.patientInfo.language', 'Language')} value={patientData.language} />
                  <Field label={t('CarePlan.patientInfo.role', 'Role')} value={patientData.role} />
                </Box>
              </Box>

              {/* AdvancedMD Match */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  AdvancedMD:
                </Typography>
                {lookupLoading && (
                  <Chip icon={<SyncIcon />} label="Matching..." color="info" size="small" variant="outlined" />
                )}
                {!lookupLoading && lookupError && (
                  <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" variant="outlined" />
                )}
                {!lookupLoading && !lookupError && candidate && (
                  <Chip icon={<CheckCircleIcon />} label="Matched" color="success" size="small" variant="filled" />
                )}
                {!lookupLoading && !lookupError && !candidate && matchPayload && (
                  <Chip icon={<WarningIcon />} label="No match" color="warning" size="small" variant="outlined" />
                )}
                {chartValue && (
                  <Typography variant="body2" fontWeight={500}>
                    Chart: {chartValue}
                  </Typography>
                )}
              </Box>

              {/* Status Chips */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {statusIndicators.map((s, i) => (
                  <Chip
                    key={i}
                    label={s.found ? s.label : `Not ${s.label}`}
                    icon={s.found ? s.icon : <CancelIcon />}
                    sx={{
                      backgroundColor: s.found ? s.color : 'grey.400',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Existing Care Plans */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader title={t('CarePlan.carePlansTitle', 'Existing Care Plans')} />
            <CardContent sx={{ p: 0 }}>
              {!Careplans || Careplans.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('CarePlan.noActiveCarePlans', 'No active care plans found.')}
                </Typography>
              ) : (
                <Paper elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Creation Date</strong></TableCell>
                        <TableCell><strong>Diagnosis(es)</strong></TableCell>
                        <TableCell><strong>Standard(s)</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Created by</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Careplans.map((plan) => (
                        <TableRow key={plan.patientCarePlanId}>
                          <TableCell>{dayjs(plan.createDate).format('DD-MM-YYYY')}</TableCell>
                          <TableCell>{plan.diagnosis}</TableCell>
                          <TableCell>{plan.standars}</TableCell>
                          <TableCell>{plan.status}</TableCell>
                          <TableCell>{plan.employee}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Memberships */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader title={t('CarePlan.Memberships.Memberships', 'Memberships')} />
            <CardContent sx={{ p: 0 }}>
              {!memberships || memberships.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('CarePlan.Memberships.noMemberships', 'No memberships found.')}
                </Typography>
              ) : (
                <Paper elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('CarePlan.Memberships.Type', 'Type')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.Plan', 'Plan')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.EffectiveDate', 'Effective Date')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.Network', 'Network')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.Group', 'Group')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.City', 'City')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.State', 'State')}</strong></TableCell>
                        <TableCell><strong>{t('CarePlan.Memberships.Status', 'Status')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {memberships.map((m, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{m.memberType}</TableCell>
                          <TableCell>{m.memberPlan}</TableCell>
                          <TableCell>{m.memberPlanEffectiveDate?.split('T')[0]}</TableCell>
                          <TableCell>{m.networkName}</TableCell>
                          <TableCell>{m.groupNumber}</TableCell>
                          <TableCell>{m.cityName}</TableCell>
                          <TableCell>{m.state}</TableCell>
                          <TableCell>{m.status === 0 ? 'Active' : 'Inactive'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Allergies (AdvancedMD) */}
          {candidate && (
            <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
              <SectionHeader title="Allergies (AdvancedMD)" />
              <CardContent sx={{ p: 0 }}>
                {allergiesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : !allergies || allergies.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No allergies found.
                  </Typography>
                ) : (
                  <Paper elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Allergy</strong></TableCell>
                          <TableCell><strong>Reaction</strong></TableCell>
                          <TableCell><strong>Treatment</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allergies.map((a, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{a.allergy || '—'}</TableCell>
                            <TableCell>{a.reaction || '—'}</TableCell>
                            <TableCell>{a.treatment || '—'}</TableCell>
                            <TableCell>{a.status || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </CardContent>
            </Card>
          )}

          {/* Clinical Notes (AdvancedMD) */}
          {candidate && (
            <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
              <SectionHeader title="Clinical Notes (AdvancedMD)" />
              <CardContent sx={{ p: 0 }}>
                {notesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : allClinicalNotes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No clinical notes found.
                  </Typography>
                ) : (
                  <Paper elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Template</strong></TableCell>
                          <TableCell><strong>Chief Complaint</strong></TableCell>
                          <TableCell><strong>Assessment</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allClinicalNotes.map((note, idx) => {
                          const f = note.fields || {};
                          const complaint = f.ChiefComplaints || f.ChiefComplaint || f.CC || '—';
                          const assessment = f.Assessments || f.AssessmentAndPlan || f.Assessment || '—';
                          const noteDate = note.noteDateTime
                            ? dayjs(note.noteDateTime).format('DD-MM-YYYY')
                            : '—';
                          return (
                            <TableRow key={idx}>
                              <TableCell>{noteDate}</TableCell>
                              <TableCell>{note._type}</TableCell>
                              <TableCell>{note.templateName || '—'}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {complaint}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {assessment}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </CardContent>
            </Card>
          )}

          {/* Create Care Plan Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<SmartToyIcon />}
              onClick={handleCreateCarePlan}
              sx={{
                backgroundColor: '#085394',
                '&:hover': { backgroundColor: '#063970' },
                textTransform: 'none',
                px: 4,
                py: 1.2,
              }}
            >
              Generate Care Plan with AI
            </Button>
          </Box>

          {/* ========= AI CARE PLAN RESULT (shown after generation) ========= */}
          {aiResult && (
            <Box sx={{ mt: 4 }}>
              <Card id="ai-care-plan-result" sx={{ mb: 3, boxShadow: 4, borderRadius: 2, overflow: 'hidden', border: '2px solid #085394' }}>
                <Box sx={{ backgroundColor: '#085394', color: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon />
                    <Typography variant="subtitle1" fontWeight="bold">
                      AI-Generated Care Plan
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Print Care Plan">
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handlePrintSection('ai-care-plan-result', 'AI-Generated Care Plan')}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy JSON to clipboard">
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={handleCopyToClipboard}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <CardContent>
                  {/* Summary */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">{aiResult.summary}</Typography>
                  </Alert>

                  {/* Risk Assessment */}
                  {aiResult.riskAssessment && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ShieldIcon color="error" />
                          <Typography fontWeight="bold">Risk Assessment</Typography>
                          <Chip
                            label={aiResult.riskAssessment.overallRisk}
                            size="small"
                            color={
                              aiResult.riskAssessment.overallRisk === 'Critical' ? 'error'
                                : aiResult.riskAssessment.overallRisk === 'High' ? 'error'
                                : aiResult.riskAssessment.overallRisk === 'Moderate' ? 'warning'
                                : 'success'
                            }
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {aiResult.riskAssessment.riskFactors?.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Risk Factors:</Typography>
                            {aiResult.riskAssessment.riskFactors.map((rf, i) => (
                              <Chip key={i} label={rf} size="small" variant="outlined" color="error" sx={{ m: 0.3 }} />
                            ))}
                          </Box>
                        )}
                        {aiResult.riskAssessment.mitigationStrategies?.length > 0 && (
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Mitigation:</Typography>
                            {aiResult.riskAssessment.mitigationStrategies.map((ms, i) => (
                              <Typography key={i} variant="body2" sx={{ pl: 1 }}>- {ms}</Typography>
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Identified Problems */}
                  {aiResult.identifiedProblems?.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalHospitalIcon color="primary" />
                          <Typography fontWeight="bold">Identified Problems ({aiResult.identifiedProblems.length})</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Problem</strong></TableCell>
                              <TableCell><strong>ICD-10</strong></TableCell>
                              <TableCell><strong>Severity</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell><strong>Evidence</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {aiResult.identifiedProblems.map((p, i) => (
                              <TableRow key={i}>
                                <TableCell>{p.problem}</TableCell>
                                <TableCell><Chip label={p.icd10 || 'N/A'} size="small" variant="outlined" /></TableCell>
                                <TableCell>
                                  <Chip
                                    label={p.severity}
                                    size="small"
                                    color={p.severity === 'Severe' ? 'error' : p.severity === 'Moderate' ? 'warning' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{p.status}</TableCell>
                                <TableCell sx={{ maxWidth: 250, fontSize: '0.75rem' }}>{p.clinicalEvidence}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Goals */}
                  {aiResult.goals?.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrackChangesIcon sx={{ color: '#ed6c02' }} />
                          <Typography fontWeight="bold">Care Plan Goals ({aiResult.goals.length})</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {aiResult.goals.map((g, i) => (
                          <Box key={i} sx={{ mb: 1.5, p: 1.5, borderLeft: '3px solid', borderColor: g.priority === 'High' ? 'error.main' : g.priority === 'Medium' ? 'warning.main' : 'success.main', bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <FlagIcon fontSize="small" color={g.priority === 'High' ? 'error' : g.priority === 'Medium' ? 'warning' : 'success'} />
                              <Typography variant="body2" fontWeight="bold">{g.goal}</Typography>
                              <Chip label={g.priority} size="small" color={g.priority === 'High' ? 'error' : g.priority === 'Medium' ? 'warning' : 'success'} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Target: {g.targetDate} | Related: {g.relatedProblem}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Interventions */}
                  {aiResult.interventions?.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AssignmentIcon color="success" />
                          <Typography fontWeight="bold">Interventions ({aiResult.interventions.length})</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Intervention</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Frequency</strong></TableCell>
                              <TableCell><strong>Responsible</strong></TableCell>
                              <TableCell><strong>Goal</strong></TableCell>
                              <TableCell><strong>Allergy Alert</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {aiResult.interventions.map((iv, i) => (
                              <TableRow key={i}>
                                <TableCell>{iv.intervention}</TableCell>
                                <TableCell><Chip label={iv.type} size="small" variant="outlined" /></TableCell>
                                <TableCell>{iv.frequency}</TableCell>
                                <TableCell>{iv.responsibleParty}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{iv.relatedGoal}</TableCell>
                                <TableCell>
                                  {iv.allergyAlert ? (
                                    <Chip label={iv.allergyAlert} size="small" color="error" variant="filled" />
                                  ) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Allergy Safety Notes */}
                  {aiResult.allergySafetyNotes?.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HealthAndSafetyIcon color="error" />
                          <Typography fontWeight="bold">Allergy Safety Notes</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {aiResult.allergySafetyNotes.map((note, i) => (
                          <Alert key={i} severity="warning" sx={{ mb: 1 }}>{note}</Alert>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Preventive Recommendations */}
                  {aiResult.preventiveRecommendations?.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HealthAndSafetyIcon color="info" />
                          <Typography fontWeight="bold">Preventive Recommendations ({aiResult.preventiveRecommendations.length})</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {aiResult.preventiveRecommendations.map((pr, i) => (
                          <Box key={i} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight="bold">{pr.recommendation}</Typography>
                            <Typography variant="caption" color="text.secondary">{pr.rationale}</Typography>
                            <Typography variant="caption" display="block">Due: {pr.dueDate}</Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Lifestyle Modifications */}
                  {aiResult.lifestyleModifications?.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FitnessCenterIcon sx={{ color: '#2e7d32' }} />
                          <Typography fontWeight="bold">Lifestyle Modifications ({aiResult.lifestyleModifications.length})</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {aiResult.lifestyleModifications.map((lm, i) => (
                          <Box key={i} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Chip label={lm.area} size="small" sx={{ mb: 0.5 }} />
                            <Typography variant="body2">{lm.recommendation}</Typography>
                            <Typography variant="caption" color="text.secondary">{lm.rationale}</Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Follow Up */}
                  {aiResult.followUp && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventNoteIcon color="primary" />
                          <Typography fontWeight="bold">Follow Up Plan</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(2, 1fr)', gap: 2 }}>
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Next Visit</Typography>
                            <Typography variant="body2">{aiResult.followUp.nextVisit || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Labs Needed</Typography>
                            {aiResult.followUp.labsNeeded?.map((lab, i) => (
                              <Chip key={i} label={lab} size="small" variant="outlined" sx={{ m: 0.3 }} />
                            ))}
                          </Box>
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Referrals</Typography>
                            {aiResult.followUp.referrals?.map((ref, i) => (
                              <Chip key={i} label={ref} size="small" color="primary" variant="outlined" sx={{ m: 0.3 }} />
                            ))}
                          </Box>
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Patient Education</Typography>
                            {aiResult.followUp.patientEducation?.map((edu, i) => (
                              <Typography key={i} variant="body2" sx={{ pl: 1 }}>- {edu}</Typography>
                            ))}
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </CardContent>
              </Card>

              {/* ========= MEAL PLAN SECTION ========= */}
              {mealPlanLoading && (
                <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden', border: '2px solid #2e7d32' }}>
                  <Box sx={{ backgroundColor: '#2e7d32', color: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RestaurantMenuIcon />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Personalized Meal Plan
                    </Typography>
                  </Box>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#2e7d32', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Generating personalized meal plan based on care plan results...
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {mealPlanError && !mealPlanLoading && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {mealPlanError}
                </Alert>
              )}

              {mealPlanResult && !mealPlanLoading && (
                <Card id="ai-meal-plan-result" sx={{ mb: 3, boxShadow: 4, borderRadius: 2, overflow: 'hidden', border: '2px solid #2e7d32' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047, #66bb6a)', color: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RestaurantMenuIcon />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Personalized Meal Plan
                      </Typography>
                    </Box>
                    <Tooltip title="Print Meal Plan">
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handlePrintSection('ai-meal-plan-result', 'Personalized Meal Plan')}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <CardContent>
                    {/* Overview */}
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2">{mealPlanResult.overview}</Typography>
                    </Alert>

                    {/* Nutrition Targets */}
                    {mealPlanResult.nutritionTargets && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FitnessCenterIcon sx={{ color: '#2e7d32' }} />
                            <Typography fontWeight="bold">Nutrition Targets</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'grid', gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 2 }}>
                            {mealPlanResult.nutritionTargets.calories && (
                              <Box sx={{ bgcolor: '#fff8e1', borderRadius: 2, p: 1.5, textAlign: 'center', border: '1px solid #ffb300' }}>
                                <Typography variant="caption" fontWeight="bold" color="#e65100">Calories</Typography>
                                <Typography variant="body2" fontWeight="bold">{mealPlanResult.nutritionTargets.calories}</Typography>
                              </Box>
                            )}
                            {mealPlanResult.nutritionTargets.sodium && (
                              <Box sx={{ bgcolor: '#fce4ec', borderRadius: 2, p: 1.5, textAlign: 'center', border: '1px solid #e91e63' }}>
                                <Typography variant="caption" fontWeight="bold" color="#c62828">Sodium</Typography>
                                <Typography variant="body2" fontWeight="bold">{mealPlanResult.nutritionTargets.sodium}</Typography>
                              </Box>
                            )}
                            {mealPlanResult.nutritionTargets.carbs && (
                              <Box sx={{ bgcolor: '#e8eaf6', borderRadius: 2, p: 1.5, textAlign: 'center', border: '1px solid #3f51b5' }}>
                                <Typography variant="caption" fontWeight="bold" color="#283593">Carbohydrates</Typography>
                                <Typography variant="body2" fontWeight="bold">{mealPlanResult.nutritionTargets.carbs}</Typography>
                              </Box>
                            )}
                            {mealPlanResult.nutritionTargets.protein && (
                              <Box sx={{ bgcolor: '#e0f2f1', borderRadius: 2, p: 1.5, textAlign: 'center', border: '1px solid #009688' }}>
                                <Typography variant="caption" fontWeight="bold" color="#00695c">Protein</Typography>
                                <Typography variant="body2" fontWeight="bold">{mealPlanResult.nutritionTargets.protein}</Typography>
                              </Box>
                            )}
                          </Box>
                          {mealPlanResult.nutritionTargets.notes && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              {mealPlanResult.nutritionTargets.notes}
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Weekly Meal Plan */}
                    {mealPlanResult.weeklyPlan?.length > 0 && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventNoteIcon sx={{ color: '#2e7d32' }} />
                            <Typography fontWeight="bold">Weekly Meal Plan ({mealPlanResult.weeklyPlan.length} days)</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {mealPlanResult.weeklyPlan.map((day, i) => (
                            <Box key={i} sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid #2e7d32' }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: '#2e7d32' }}>
                                {day.day}
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(3, 1fr)', gap: 2 }}>
                                {/* Breakfast */}
                                <Box sx={{ bgcolor: '#fff8e1', borderRadius: 1, p: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <FreeBreakfastIcon fontSize="small" sx={{ color: '#e65100' }} />
                                    <Typography variant="caption" fontWeight="bold" color="#e65100">Breakfast</Typography>
                                  </Box>
                                  <Typography variant="body2">{day.breakfast?.meal || '—'}</Typography>
                                  {day.breakfast?.notes && (
                                    <Typography variant="caption" color="text.secondary" display="block">{day.breakfast.notes}</Typography>
                                  )}
                                </Box>
                                {/* Lunch */}
                                <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 1, p: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <LunchDiningIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                    <Typography variant="caption" fontWeight="bold" color="#2e7d32">Lunch</Typography>
                                  </Box>
                                  <Typography variant="body2">{day.lunch?.meal || '—'}</Typography>
                                  {day.lunch?.notes && (
                                    <Typography variant="caption" color="text.secondary" display="block">{day.lunch.notes}</Typography>
                                  )}
                                </Box>
                                {/* Dinner */}
                                <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 1, p: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <DinnerDiningIcon fontSize="small" sx={{ color: '#1565c0' }} />
                                    <Typography variant="caption" fontWeight="bold" color="#1565c0">Dinner</Typography>
                                  </Box>
                                  <Typography variant="body2">{day.dinner?.meal || '—'}</Typography>
                                  {day.dinner?.notes && (
                                    <Typography variant="caption" color="text.secondary" display="block">{day.dinner.notes}</Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Foods to Avoid */}
                    {mealPlanResult.foodsToAvoid?.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HealthAndSafetyIcon color="error" />
                            <Typography fontWeight="bold">Foods to Avoid ({mealPlanResult.foodsToAvoid.length})</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Food / Ingredient</strong></TableCell>
                                <TableCell><strong>Reason</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {mealPlanResult.foodsToAvoid.map((f, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    <Chip label={f.food} size="small" color="error" variant="outlined" />
                                  </TableCell>
                                  <TableCell>{f.reason}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Allergy Safe Alternatives */}
                    {mealPlanResult.allergySafeAlternatives?.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShieldIcon color="info" />
                            <Typography fontWeight="bold">Allergy-Safe Alternatives</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Allergen</strong></TableCell>
                                <TableCell><strong>Safe Alternatives</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {mealPlanResult.allergySafeAlternatives.map((a, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    <Chip label={a.allergen} size="small" color="warning" variant="filled" />
                                  </TableCell>
                                  <TableCell>{a.alternatives}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Snack Suggestions */}
                    {mealPlanResult.snackSuggestions?.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RestaurantMenuIcon sx={{ color: '#ed6c02' }} />
                            <Typography fontWeight="bold">Snack Suggestions</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {mealPlanResult.snackSuggestions.map((snack, i) => (
                              <Chip key={i} label={snack} variant="outlined" color="success" />
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Hydration Guidelines */}
                    {mealPlanResult.hydrationGuidelines && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalDrinkIcon sx={{ color: '#0288d1' }} />
                            <Typography fontWeight="bold">Hydration Guidelines</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ bgcolor: '#e1f5fe', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <LocalDrinkIcon sx={{ color: '#0288d1', fontSize: 32 }} />
                            <Typography variant="body2">{mealPlanResult.hydrationGuidelines}</Typography>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Food Traffic Light */}
                    {mealPlanResult.foodTrafficLight && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrafficIcon sx={{ color: '#f44336' }} />
                            <Typography fontWeight="bold">Food Traffic Light</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(2, 1fr)', gap: 2 }}>
                            {/* Green */}
                            {mealPlanResult.foodTrafficLight.green?.length > 0 && (
                              <Box sx={{ border: '2px solid #4caf50', borderRadius: 2, p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: 11 }}>GO</Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight="bold" color="#2e7d32">Can Eat Freely</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {mealPlanResult.foodTrafficLight.green.map((item, i) => (
                                    <Tooltip key={i} title={item.reason || ''} arrow>
                                      <Chip label={item.food} size="small" sx={{ bgcolor: '#c8e6c9', color: '#1b5e20', fontWeight: 500 }} />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {/* Yellow */}
                            {mealPlanResult.foodTrafficLight.yellow?.length > 0 && (
                              <Box sx={{ border: '2px solid #fdd835', borderRadius: 2, p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#fdd835', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: 9 }}>MOD</Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight="bold" color="#f9a825">Eat in Moderation</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {mealPlanResult.foodTrafficLight.yellow.map((item, i) => (
                                    <Tooltip key={i} title={item.reason || ''} arrow>
                                      <Chip label={item.food} size="small" sx={{ bgcolor: '#fff9c4', color: '#f57f17', fontWeight: 500 }} />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {/* Orange */}
                            {mealPlanResult.foodTrafficLight.orange?.length > 0 && (
                              <Box sx={{ border: '2px solid #ff9800', borderRadius: 2, p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#ff9800', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: 10 }}>!</Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight="bold" color="#e65100">Preferably Avoid</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {mealPlanResult.foodTrafficLight.orange.map((item, i) => (
                                    <Tooltip key={i} title={item.reason || ''} arrow>
                                      <Chip label={item.food} size="small" sx={{ bgcolor: '#ffe0b2', color: '#bf360c', fontWeight: 500 }} />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {/* Red */}
                            {mealPlanResult.foodTrafficLight.red?.length > 0 && (
                              <Box sx={{ border: '2px solid #f44336', borderRadius: 2, p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#f44336', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: 10 }}>X</Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight="bold" color="#c62828">Must Avoid Completely</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {mealPlanResult.foodTrafficLight.red.map((item, i) => (
                                    <Tooltip key={i} title={item.reason || ''} arrow>
                                      <Chip label={item.food} size="small" sx={{ bgcolor: '#ffcdd2', color: '#b71c1c', fontWeight: 500 }} />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </>
      )}

      {/* ========= AI CARE PLAN DIALOG (Wizard) ========= */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#085394', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon />
          {wizardMode === null && 'Create AI-Powered Care Plan'}
          {wizardMode === 'carePlanOnly' && 'Care Plan — Goals & Habits'}
          {wizardMode === 'carePlanAndMeal' && 'Care Plan & Meal Plan'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* ---- Mode Selection ---- */}
          {wizardMode === null && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  The AI will use:
                </Typography>
                <Typography variant="body2" component="div">
                  - <strong>{allClinicalNotes.length}</strong> clinical notes from AdvancedMD
                  <br />
                  - <strong>{allergies?.length || 0}</strong> allergy records
                  <br />
                  - Patient demographics and clinical history
                </Typography>
              </Alert>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
                What would you like to generate?
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(2, 1fr)', gap: 2 }}>
                {/* Care Plan Only */}
                <Card
                  onClick={() => handleSelectMode('carePlanOnly')}
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': { border: '2px solid #085394', boxShadow: 4 },
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 48, color: '#085394', mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="#085394">
                    Care Plan Only
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Generate a comprehensive AI care plan with goals, interventions, and follow-up.
                  </Typography>
                </Card>

                {/* Care Plan & Meal Plan */}
                <Card
                  onClick={() => handleSelectMode('carePlanAndMeal')}
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': { border: '2px solid #2e7d32', boxShadow: 4 },
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <SmartToyIcon sx={{ fontSize: 48, color: '#085394' }} />
                    <RestaurantMenuIcon sx={{ fontSize: 48, color: '#2e7d32' }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="#2e7d32">
                    Care Plan & Meal Plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Generate care plan + personalized 7-day meal plan with food traffic light.
                  </Typography>
                </Card>
              </Box>
            </>
          )}

          {/* ---- Care Plan Only: single screen ---- */}
          {wizardMode === 'carePlanOnly' && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Using <strong>{allClinicalNotes.length}</strong> clinical notes and <strong>{allergies?.length || 0}</strong> allergy records.
                </Typography>
              </Alert>

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Care Plan Goals
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Describe the goals you want to achieve (e.g. manage diabetes, reduce ER visits, medication adherence).
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g. Improve HbA1c control below 7%, reduce hospital readmissions, optimize medication regimen..."
                value={aiGoals}
                onChange={(e) => setAiGoals(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Divider sx={{ mb: 3 }} />

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Patient Habits / Lifestyle
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Note any relevant lifestyle habits (diet, exercise, smoking, alcohol, sleep patterns).
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g. Sedentary lifestyle, smokes 1 pack/day for 20 years, high-sodium diet..."
                value={aiHabits}
                onChange={(e) => setAiHabits(e.target.value)}
                sx={{ mb: 2 }}
              />

              {aiError && (
                <Alert severity="error" sx={{ mt: 2 }}>{aiError}</Alert>
              )}
            </>
          )}

          {/* ---- Care Plan & Meal Plan: Wizard ---- */}
          {wizardMode === 'carePlanAndMeal' && (
            <>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                <Step>
                  <StepLabel>Goals & Habits</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Dietary & Cultural</StepLabel>
                </Step>
              </Stepper>

              {/* Step 0: Goals & Habits */}
              {activeStep === 0 && (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Using <strong>{allClinicalNotes.length}</strong> clinical notes and <strong>{allergies?.length || 0}</strong> allergy records.
                    </Typography>
                  </Alert>

                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Care Plan Goals
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Describe the goals you want to achieve (e.g. manage diabetes, reduce ER visits, medication adherence).
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g. Improve HbA1c control below 7%, reduce hospital readmissions, optimize medication regimen..."
                    value={aiGoals}
                    onChange={(e) => setAiGoals(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  <Divider sx={{ mb: 3 }} />

                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Patient Habits / Lifestyle
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Note any relevant lifestyle habits (diet, exercise, smoking, alcohol, sleep patterns).
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g. Sedentary lifestyle, smokes 1 pack/day for 20 years, high-sodium diet..."
                    value={aiHabits}
                    onChange={(e) => setAiHabits(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* Step 1: Dietary & Cultural */}
              {activeStep === 1 && (
                <>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RestaurantMenuIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                    Dietary Preferences / Restrictions
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Note any dietary restrictions or preferences (e.g. low-sodium, lactose intolerant, vegetarian, gluten-free, diabetic diet, renal diet).
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g. Low-sodium diet, lactose intolerant, needs high-fiber foods, diabetic-friendly meals..."
                    value={aiDietaryPreferences}
                    onChange={(e) => setAiDietaryPreferences(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  <Divider sx={{ mb: 3 }} />

                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RestaurantMenuIcon fontSize="small" sx={{ color: '#ed6c02' }} />
                    Cultural / Regional Diet
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Describe the patient's cultural or regional food traditions (e.g. Colombian cuisine, Haitian diet, Mexican food, Caribbean, Mediterranean).
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g. Colombian cuisine (rice, beans, arepas), prefers traditional Haitian dishes, Mexican food traditions..."
                    value={aiCulturalDiet}
                    onChange={(e) => setAiCulturalDiet(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  {aiError && (
                    <Alert severity="error" sx={{ mt: 2 }}>{aiError}</Alert>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {/* Mode selection: only Cancel */}
          {wizardMode === null && (
            <Button onClick={handleCloseDialog} color="inherit" sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          )}

          {/* Care Plan Only: Back + Generate */}
          {wizardMode === 'carePlanOnly' && (
            <>
              <Button onClick={handleWizardBack} color="inherit" sx={{ textTransform: 'none' }}>
                Back
              </Button>
              <Button
                variant="contained"
                startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <SmartToyIcon />}
                onClick={handleGenerateAiPlan}
                disabled={aiLoading || (!aiGoals.trim() && !aiHabits.trim())}
                sx={{
                  backgroundColor: '#085394',
                  '&:hover': { backgroundColor: '#063970' },
                  textTransform: 'none',
                }}
              >
                {aiLoading ? 'Generating...' : 'Generate Care Plan'}
              </Button>
            </>
          )}

          {/* Care Plan & Meal Plan wizard: Back + Next/Generate */}
          {wizardMode === 'carePlanAndMeal' && (
            <>
              <Button onClick={handleWizardBack} color="inherit" sx={{ textTransform: 'none' }}>
                Back
              </Button>
              {activeStep === 0 ? (
                <Button
                  variant="contained"
                  onClick={handleWizardNext}
                  disabled={!aiGoals.trim() && !aiHabits.trim()}
                  sx={{
                    backgroundColor: '#2e7d32',
                    '&:hover': { backgroundColor: '#1b5e20' },
                    textTransform: 'none',
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <SmartToyIcon />}
                  onClick={handleGenerateAiPlan}
                  disabled={aiLoading || (!aiGoals.trim() && !aiHabits.trim())}
                  sx={{
                    backgroundColor: '#2e7d32',
                    '&:hover': { backgroundColor: '#1b5e20' },
                    textTransform: 'none',
                  }}
                >
                  {aiLoading ? 'Generating...' : 'Generate Care Plan & Meal Plan'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Careplan;
